import React from 'react';
import styled from 'styled-components';

import type { BoardSummary } from './accountState';
import { boardListDisplay } from './boardNaming';

/**
 * 🌷 Mom · ⛳ Dad · 🎀 Emma · +1 — the "two or more drops the name and shows the
 * people instead" half of the naming rule.
 *
 * Presentational and shared by the capture sheet, the You card and the claim
 * page on purpose: three surfaces render this list and they must not disagree
 * about truncation, order or the overflow count (the rule is "truncated at
 * three, newest first").
 *
 * Emoji arrive resolved. Resolution is FE-only and lives in
 * `resolveRecipientEmoji` (architecture.md — the BE deliberately carries no
 * relationship→emoji table), so this component takes what it is given rather
 * than growing a second opinion about it.
 */

const Row = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Person = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px 5px 9px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.color.cream};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  line-height: 1.2;
  color: hsl(var(--foreground));
  max-width: 100%;
`;

const Name = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Overflow = styled.li`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  color: ${({ theme }) => theme.color.mutedText};
`;

export interface BoardPeopleRowProps {
  /** Newest first. Truncation + overflow are handled here. */
  boards: BoardSummary[];
  className?: string;
}

export const BoardPeopleRow: React.FC<BoardPeopleRowProps> = ({
  boards,
  className,
}) => {
  const { visible, overflow } = boardListDisplay(boards);
  if (visible.length === 0) return null;
  return (
    <Row className={className} data-testid="board-people-row">
      {visible.map((board) => (
        <Person key={board.id}>
          <span aria-hidden="true">{board.emoji}</span>
          <Name>{board.name}</Name>
        </Person>
      ))}
      {overflow > 0 ? <Overflow data-testid="board-overflow">+{overflow}</Overflow> : null}
    </Row>
  );
};

export default BoardPeopleRow;
